import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, PencilSimple, Check } from "@phosphor-icons/react";

/**
 * Tag list editor with add / remove / rename support.
 *
 * Props:
 *  - label, hint, values, setValues, testid, disabled — the additive/remove behaviour
 *  - onRename?(from, to) — async callback. When provided, each chip gets a pencil
 *    icon and clicking it lets the admin rename that value in-place. Parent is
 *    responsible for calling the backend and updating both `values` and any
 *    saved snapshot so the "unsaved changes" bar doesn't fire on a rename.
 */
export function TagListEditor({ label, hint, values, setValues, testid, disabled, onRename }) {
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null); // value currently being renamed
  const [renameDraft, setRenameDraft] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(""); return; }
    setValues([...values, v]);
    setDraft("");
  };
  const remove = (v) => setValues(values.filter((x) => x !== v));

  const startEdit = (v) => {
    setEditing(v);
    setRenameDraft(v);
  };
  const cancelEdit = () => {
    setEditing(null);
    setRenameDraft("");
  };
  const saveEdit = async () => {
    const from = editing;
    const to = renameDraft.trim();
    if (!from || !to || from === to) { cancelEdit(); return; }
    if (!onRename) {
      // Fall back to local rename only (no backend cascade)
      setValues(values.map((v) => (v === from ? to : v)));
      cancelEdit();
      return;
    }
    setSavingRename(true);
    try {
      await onRename(from, to);
      cancelEdit();
    } finally { setSavingRename(false); }
  };

  return (
    <div>
      <div className="text-sm font-medium">{label}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      <div className="mt-2 flex flex-wrap gap-1.5 items-center">
        {values.map((v) => {
          const isEditingThis = editing === v;
          if (isEditingThis) {
            return (
              <span
                key={v}
                className="inline-flex items-center gap-1 bg-white border border-[#4f4c90]/40 rounded-md pl-2 pr-1 py-0.5"
                data-testid={`${testid}-editing-${v}`}
              >
                <Input
                  autoFocus
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); saveEdit(); }
                    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                  }}
                  disabled={savingRename}
                  className="h-6 text-xs w-40 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none px-1"
                  data-testid={`${testid}-rename-input`}
                />
                <button
                  onClick={saveEdit}
                  disabled={savingRename}
                  className="p-0.5 text-[#39A36E] hover:text-[#2E8C5D]"
                  data-testid={`${testid}-rename-save`}
                  aria-label="Save rename"
                >
                  <Check size={12} weight="bold" />
                </button>
                <button
                  onClick={cancelEdit}
                  disabled={savingRename}
                  className="p-0.5 text-slate-400 hover:text-red-500"
                  data-testid={`${testid}-rename-cancel`}
                  aria-label="Cancel rename"
                >
                  <X size={12} weight="bold" />
                </button>
              </span>
            );
          }
          return (
            <span key={v} className="chip !text-slate-800 !border-slate-300 bg-slate-100 gap-1 pl-2 pr-1 py-0.5">
              {v}
              {!disabled && (
                <>
                  <button
                    onClick={() => startEdit(v)}
                    className="hover:text-[#4f4c90] p-0.5"
                    data-testid={`${testid}-edit-${v}`}
                    aria-label={`Rename ${v}`}
                    title="Rename"
                  >
                    <PencilSimple size={10} weight="bold" />
                  </button>
                  <button
                    onClick={() => remove(v)}
                    className="hover:text-red-400 p-0.5"
                    data-testid={`${testid}-remove-${v}`}
                    aria-label={`Remove ${v}`}
                    title="Remove"
                  >
                    <X size={10} weight="bold" />
                  </button>
                </>
              )}
            </span>
          );
        })}
        {values.length === 0 && <span className="text-xs text-slate-500 italic">None — the list will fall back to defaults on save.</span>}
      </div>
      {!disabled && (
        <div className="mt-2 flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Add and press Enter"
            className="bg-[#FFFFFF] border-slate-200 h-8 text-sm max-w-xs"
            data-testid={`${testid}-input`}
          />
          <Button
            size="sm"
            variant="outline"
            className="border-slate-200 bg-slate-100 h-8 gap-1"
            onClick={add}
            data-testid={`${testid}-add`}
          >
            <Plus size={12} /> Add
          </Button>
        </div>
      )}
    </div>
  );
}
